import './PdfPreview.scss';

import React, { memo, useCallback, useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import Loader from 'atoms/Loader/Loader';
import useZoom from 'hooks/useZoom';
import Resizeable from 'organisms/PdfGenerator/Resizeable';
import ViewControls, { PageControls } from 'organisms/PdfGenerator/ViewControls';

import PreviewError from '../PreviewError';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const pdfOptions = {
  cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
  cMapPacked: true,
};

function PdfPreview({ file, name, isExtended }) {
  const [pages, setPages] = useState();
  const [isSuccess, setIsSuccess] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { zoom } = useZoom(1, 0.5, 2, 0.25);
  const [width, setWidth] = useState();
  const fileObj = useMemo(() => ({ url: file }), [file]);

  const handleLoadSuccess = useCallback(({ numPages }) => {
    setPages(numPages);
    setIsSuccess(true);
  }, []);

  const handleLoadError = useCallback((error) => {
    // eslint-disable-next-line no-console
    console.error('[PdfPreview] PDF load error:', error);
    setIsSuccess(false);
  }, []);

  const handlePreviousClick = () => {
    if (currentPage !== 1) {
      setCurrentPage((pg) => pg - 1);
    }
  };

  const handleNextClick = () => {
    if (currentPage < pages) {
      setCurrentPage((pg) => pg + 1);
    }
  };

  const handleResize = useCallback(
    (_width) => {
      setWidth(_width * (isExtended ? 0.6 : 0.8));
    },
    [isExtended],
  );

  return (
    <Resizeable onResize={handleResize}>
      <div className="PdfPreview">
        <Document
          file={fileObj}
          options={pdfOptions}
          onLoadSuccess={handleLoadSuccess}
          onLoadError={handleLoadError}
          error={<PreviewError fileName={name} />}
          loading={<Loader />}
          className="PdfPreview__document"
        >
          <Page
            pageNumber={currentPage}
            width={width ? Math.round(width * zoom) : undefined}
            rotate={0}
            className="PdfPreview__file"
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
        {isSuccess && (
          <ViewControls className="PdfPreview__controls">
            <PageControls
              currentPage={currentPage}
              pages={pages}
              onPreviousClick={handlePreviousClick}
              onNextClick={handleNextClick}
            />
          </ViewControls>
        )}
      </div>
    </Resizeable>
  );
}

export default memo(PdfPreview);
